import React, { useState, useEffect } from 'react';
import { db } from "../firebase-config";
import { collection, getDocs } from "firebase/firestore";
import "./CSS/NewsUpdate.css";
import AnimatedHero from '../Components/AnimatedHero';
import Loader from '../Components/Loader';

const formatDate = (timestamp) => {
  if (!timestamp) return "";
  
  const date = timestamp.seconds 
    ? new Date(timestamp.seconds * 1000)
    : new Date(timestamp);
  
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const NewsUpdate = () => {
  const [news, setNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [selectedTag, setSelectedTag] = useState("all");
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;

  useEffect(() => {
    const fetchNews = async () => {
      try {
        setLoading(true);
        const snapshot = await getDocs(collection(db, "news"));
        let data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        data = data.sort((a, b) => {
          const timeA = a.timestamp?.seconds || 0;
          const timeB = b.timestamp?.seconds || 0;
          return timeB - timeA;
        });
        
        setNews(data);
        setFilteredNews(data);
      } catch (error) {
        console.error("Error fetching news:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  useEffect(() => {
    if (selectedTag === "all") {
      setFilteredNews(news);
    } else {
      setFilteredNews(news.filter(item => 
        item.tags && item.tags.includes(selectedTag)
      ));
    }
    setCurrentPage(1);
  }, [selectedTag, news]);

  const allTags = [...new Set(news.flatMap(item => item.tags || []))];
  
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredNews.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);

  const featuredNews = news.filter(item => item.featured).slice(0, 2);

  if (loading) {
    return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh'
    }}>
      <Loader />
    </div>
  );}

  return (
    <div className="news-page bg-background">
      <AnimatedHero
        title="Making a Difference"
        subtitle="Stay updated with our latest initiatives, success stories, and community impact"
        className="about-hero"
        overlayColor="rgba(14,77,109,0.15)"
      >
        <div className="d-flex justify-content-center gap-2 flex-wrap mt-4">
          <button
            className={`btn ${selectedTag === "all" ? "btn-accent" : "btn-outline-light"}`}
            onClick={() => setSelectedTag("all")}
          >
            All NEWS
          </button>
          {allTags.map(tag => (
            <button
              key={tag}
              className={`btn ${selectedTag === tag ? "btn-accent" : "btn-outline-light"}`}
              onClick={() => setSelectedTag(tag)}
            >
              {tag}
            </button>
          ))}
        </div>
      </AnimatedHero>

{/* Breaking News Ticker - Paste this anywhere in your JSX */}
{featuredNews.length > 0 && (
  <div className="bg-danger py-2 overflow-hidden" style={{ 
    position: 'relative',
    background: 'linear-gradient(90deg, var(--primary) 0%, var(--secondary-light) 100%)'
  }}>
    <div className="container-fluid px-0">
      <div className="d-flex align-items-center">
        {/* Breaking News Label */}
        <div className="d-flex align-items-center me-3 ps-2" style={{ minWidth: '140px' }}>
          <span className="badge bg-white text-primary fw-bold fs-6 me-2 px-3 py-1">BREAKING</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#fff" viewBox="0 0 16 16">
            <path d="M7.27 1.047a1 1 0 0 1 1.46 0l6.345 6.77c.6.638.146 1.683-.73 1.683H11.5v3a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1v-3H1.654C.78 9.5.326 8.455.926 7.816L7.27 1.047z"/>
          </svg>
        </div>
        
        {/* News Ticker */}
        <div className="position-relative overflow-hidden flex-grow-1" style={{ height: '24px' }}>
          <div className="position-absolute w-100" style={{
            animation: 'ticker 30s linear infinite',
            whiteSpace: 'nowrap'
          }}>
            {/* Duplicate content for seamless looping */}
            {[...featuredNews, ...featuredNews].map((item, index) => (
              <React.Fragment key={`${item.id}-${index}`}>
                <a 
                  href={item.sourceUrl || `/news/${item.id}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-white px-3 fw-semibold"
                  style={{ 
                    textDecoration: 'none',
                    borderRight: '1px solid rgba(255,255,255,0.5)',
                    display: 'inline-block',
                    lineHeight: '24px'
                  }}
                >
                  {item.title}
                </a>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>

    <style>{`
      @keyframes ticker {
        0% { transform: translateX(0); }
        100% { transform: translateX(-50%); }
      }
      .text-white:hover { text-decoration: underline !important; }
    `}</style>
  </div>
)}

      {/* All News */}
      <section className="py-5">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="fw-bold primary">News Updates</h2>
            <div className="text-secondary bold">
              <span className='font-semibold'>Showing {Math.min(itemsPerPage, currentItems.length)} of {filteredNews.length} stories</span>
            </div>
          </div>
          
          <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
            {currentItems.length > 0 ? (
              currentItems.map(item => (
                <div key={item.id} className="col">
                  <div className="bg-light card h-100 border-0 shadow-sm overflow-hidden">
                    {item.imageUrl && (
                      <div 
                        className="news-card-img"
                        style={{
                          backgroundImage: `url(${item.imageUrl})`,
                          height: '200px'
                        }}
                      ></div>
                    )}
                    
                    <div className="card-body p-4 d-flex flex-column">
                      <div>
                        <h5 className="card-title fw-bold mb-3 text-truncate">{item.title}</h5>
                        
                        
                        
                        <p className="card-text secondary-light line-clamp-3 mb-4">
                          {item.shortDescription || 
                          `${item.description.substring(0, 150)}${item.description.length > 150 ? "..." : ""}`}
                        </p>
                      </div>
                      
                      <div style={{ marginTop: 'auto', paddingTop: '1rem' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            gap: '1rem' // adds spacing between date and button
                          }}
                        >
                          <small style={{ color: '#6c757d', flexShrink: 0 }}>
                            {formatDate(item.timestamp)}
                          </small>

                          <div style={{ marginLeft: 'auto' }}>
                            <a 
                              href={`/news/${item.id}`} 
                              className="btn-sm btn-accent"
                              style={{ textDecoration: 'none' }}
                            >
                              View Details
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 py-5 text-center">
                <div className="bg-white p-5 rounded-3 shadow-sm">
                  <i className="bi bi-newspaper display-1 text-muted mb-4"></i>
                  <h3 className="mb-3">No News Found</h3>
                  <p className="secondary-light mb-4">
                    There are no news items matching your selection
                  </p>
                  <button 
                    className="btn-primary"
                    onClick={() => setSelectedTag("all")}
                  >
                    View All News
                  </button>
                </div>
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="mt-5 d-flex justify-content-center">
              <ul className="pagination">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button 
                    className="page-link" 
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </li>
                
                {[...Array(totalPages).keys()].map(num => (
                  <li 
                    key={num} 
                    className={`page-item ${currentPage === num + 1 ? "active" : ""}`}
                  >
                    <button 
                      className="page-link" 
                      onClick={() => setCurrentPage(num + 1)}
                    >
                      {num + 1}
                    </button>
                  </li>
                ))}
                
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button 
                    className="page-link" 
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-5" style={{ backgroundColor: "var(--primary)" }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6 mb-4 mb-md-0">
              <h3 className="text-accent">Stay Connected</h3>
              <p className="text-light">
                Subscribe to our newsletter for regular updates on our initiatives
              </p>
            </div>
            <div className="col-md-6">
              <div className="input-group">
                <input 
                  type="email" 
                  className="form-control form-control-lg" 
                  placeholder="Your email address"
                  style={{ borderColor: "var(--accent)" }}
                />
                <button 
                  className="btn" 
                  type="button" 
                  style={{ backgroundColor: "var(--accent)", color: "var(--primary)" }}
                >
                  Subscribe
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsUpdate;